import { Input, MessageBarBody, MessageBarTitle } from '@fluentui/react-components';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '../../../../../redux/reducers';
import { ErrorMessageComponent } from '../../../../Error/ErrorMessageComponent';

// i18Next
import { useTranslation, Trans } from 'react-i18next';

// store
import * as ConceptCategoryStore from '../../../../../redux/middleware/ConceptCategoryMiddleware';
import {
  ContractConceptCategoryFormProps,
  FormValidation,
  buildCreatePayload,
  buildUpdatePayload,
  validateForm
} from './ContractConceptCategory.Form.Functions';
import { GuideDrawer, GuideDrawerForm } from '../../../../../theme/components/GuideDrawer';
import { GuideField } from '../../../../../theme/components/GuideField';
import { GuideMessageBar } from '../../../../../theme/components/GuideMessageBar';

export const ContractConceptCategoryFormComponent = (props: ContractConceptCategoryFormProps) => {
  const [submittedForm, setSubmittedForm] = useState<boolean>(false);
  const [formValidationStatus, setFormValidationStatus] = useState<FormValidation | undefined>();

  const dispatch = useDispatch();
  const [t] = useTranslation();

  const conceptCategoryState = useSelector((appState: AppState) => appState.ConceptCategories);

  const [conceptCategoryName, setConceptCategoryName] = useState<string>('');
  const [conceptCategoryDescription, setConceptCategoryDescription] = useState<string>('');

  useEffect(() => {
    if (props.ConceptCategory) {
      setConceptCategoryName(props.ConceptCategory.conceptCategoryName || '');
      setConceptCategoryDescription(props.ConceptCategory.conceptCategoryDescription || '');
    } else {
      resetForm();
    }
  }, [props.ConceptCategory]);

  useEffect(() => {
    if (
      submittedForm &&
      conceptCategoryState &&
      (conceptCategoryState.successAddingNewOne || conceptCategoryState.successUpdatingOne)
    ) {
      setTimeout(() => {
        resetForm();
        props.dismissPanel();
      }, 100);
    }
  }, [
    submittedForm,
    conceptCategoryState?.successAddingNewOne,
    conceptCategoryState?.successUpdatingOne,
    props.dismissPanel
  ]);

  const resetForm = () => {
    setConceptCategoryName('');
    setConceptCategoryDescription('');
    setSubmittedForm(false);
    setFormValidationStatus(undefined);
  };

  const onChangeConceptCategoryName = (ev: React.ChangeEvent<HTMLInputElement>) => {
    setConceptCategoryName(ev.target.value);
  };

  const onChangeConceptCategoryDescription = (ev: React.ChangeEvent<HTMLInputElement>) => {
    setConceptCategoryDescription(ev.target.value);
  };

  const handleSaveForm = () => {
    setSubmittedForm(true);

    const validation = validateForm(
      {
        conceptCategoryName,
        conceptCategoryDescription
      },
      t
    );

    setFormValidationStatus(validation);

    if (!validation.success) {
      return;
    }

    if (props.ConceptCategory) {
      const updatedCategory = buildUpdatePayload(
        props.ConceptCategory,
        conceptCategoryName,
        conceptCategoryDescription
      );
      dispatch(ConceptCategoryStore.actionCreators.UpdateConceptCategory(updatedCategory));
    } else {
      const newCategory = buildCreatePayload(
        conceptCategoryName,
        conceptCategoryDescription
      );
      dispatch(ConceptCategoryStore.actionCreators.AddConceptCategory(newCategory));
    }
  };

  const handleCancelForm = () => {
    resetForm();
    props.dismissPanel();
  };

  const isSaving = !!(conceptCategoryState?.isAddingNewOne || conceptCategoryState?.isUpdatingOne);

  return (
    <GuideDrawer
      open={props.isOpen}
      size="small"
      title={
        props.ConceptCategory
          ? t('contractConceptCategoryForm.titleEdit')
          : t('contractConceptCategoryForm.title')
      }
      onDismiss={handleCancelForm}
      testId="contract-concept-category-form-drawer"
      secondaryAction={{
        label: <Trans i18nKey={'contractConceptCategoryForm.cancelButton'} />,
        onClick: handleCancelForm
      }}
      primaryAction={{
        label: <Trans i18nKey={'contractConceptCategoryForm.saveButton'} />,
        onClick: handleSaveForm,
        loading: isSaving
      }}
    >
      {submittedForm && !formValidationStatus?.success && (
        <GuideMessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>{t('contractConceptCategoryForm.reviewInformation')}</MessageBarTitle>
            <ul>
              {formValidationStatus?.errors.map((error: string) => (
                <li key={error}>{error}</li>
              ))}
            </ul>
          </MessageBarBody>
        </GuideMessageBar>
      )}

      {conceptCategoryState &&
        (conceptCategoryState.failOnAddingNewOne || conceptCategoryState.failOnUpdatingOne) &&
        conceptCategoryState.error && (
          <ErrorMessageComponent Error={conceptCategoryState.error} />
        )}

      <GuideDrawerForm onSubmit={handleSaveForm}>
        <GuideField
          label={t('contractConceptCategoryForm.fields.name')}
          required
          error={
            submittedForm && (!conceptCategoryName || conceptCategoryName.trim() === '')
              ? t('contractConceptCategoryForm.validation.requiredName')
              : undefined
          }
        >
          <Input
            name="txt_concept_category_name"
            onChange={onChangeConceptCategoryName}
            value={conceptCategoryName}
            placeholder={t('contractConceptCategoryForm.fields.namePlaceholder')}
          />
        </GuideField>

        <GuideField label={t('contractConceptCategoryForm.fields.description')}>
          <Input
            name="txt_concept_category_description"
            onChange={onChangeConceptCategoryDescription}
            value={conceptCategoryDescription}
            placeholder={t('contractConceptCategoryForm.fields.descriptionPlaceholder')}
          />
        </GuideField>
      </GuideDrawerForm>
    </GuideDrawer>
  );
};

export default ContractConceptCategoryFormComponent;
